import { useState } from 'react';
import { ArrowLeft, ArrowDownRight, ArrowUpRight, Calendar, SlidersHorizontal } from 'lucide-react';
import { Loading } from '@/core/ui/components/Loading';
import { formatCurrency } from '@yawi/infrastructure';
import type { StatementGroup } from '@yawi/types';
import { useMonthlyStatement } from '../hooks/useMonthlyStatement';
import { ReportExportButton } from '../components';

interface MonthlyStatementPageProps {
  onBack: () => void;
}

function StatementSection({
  title,
  groups,
  total,
  totalLabel,
  accent,
}: {
  title: string;
  groups: StatementGroup[];
  total: number;
  totalLabel: string;
  accent: 'green' | 'red';
}) {
  const accentBg = accent === 'green' ? 'bg-[#6FA544]' : 'bg-[#DD7C7B]';

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 bg-[#8E88D6] text-white">
        <span className="font-semibold text-sm">{title}</span>
        <span className="font-semibold text-sm">Cantidad</span>
      </div>

      {groups.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-400">Sin registros en el periodo</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {groups.map((group) => (
            <div key={group.name}>
              <div className="flex items-center justify-between px-5 py-2.5 bg-[#F4F3FB]">
                <span className="font-semibold text-sm text-slate-800">{group.name}</span>
                <span className="font-semibold text-sm text-slate-800">
                  {formatCurrency(group.total)}
                </span>
              </div>
              {group.lines.map((line, i) => (
                <div
                  key={`${group.name}-${i}`}
                  className="flex items-center justify-between pl-9 pr-5 py-1.5"
                >
                  <span className="text-[13px] text-slate-500">{line.concept}</span>
                  <span className="text-[13px] text-slate-700 tabular-nums">
                    {formatCurrency(line.amount)}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className={`flex items-center justify-between px-5 py-3 text-white ${accentBg}`}>
        <span className="font-bold text-sm">{totalLabel}</span>
        <span className="font-bold text-sm">{formatCurrency(total)}</span>
      </div>
    </div>
  );
}

export const MonthlyStatementPage = ({ onBack }: MonthlyStatementPageProps) => {
  const { report, loading, exporting, params, setMonth, setManualFields, exportReport } =
    useMonthlyStatement();
  const [showManual, setShowManual] = useState(false);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Estado Financiero Mensual</h2>
            <p className="text-slate-500 text-sm">Ingresos y egresos con detalle y resumen</p>
          </div>
        </div>

        <ReportExportButton onExport={exportReport} loading={exporting} disabled={loading || !report} />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <Calendar size={16} />
              Mes
            </label>
            <input
              type="month"
              value={params.month}
              onChange={(e) => setMonth(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={() => setShowManual((v) => !v)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            <SlidersHorizontal size={16} />
            Saldos manuales
          </button>
        </div>

        {showManual && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Saldo inicial</label>
              <input
                type="number"
                value={params.openingBalance ?? ''}
                onChange={(e) =>
                  setManualFields({
                    openingBalance: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                placeholder="0.00"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Etiqueta banco 1</label>
              <input
                type="text"
                value={params.bankLabel1 ?? ''}
                onChange={(e) => setManualFields({ bankLabel1: e.target.value || undefined })}
                placeholder="Saldo Banorte"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Saldo banco 1</label>
              <input
                type="number"
                value={params.bankBalance1 ?? ''}
                onChange={(e) =>
                  setManualFields({
                    bankBalance1: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                placeholder="0.00"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
            <div className="lg:col-start-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Etiqueta banco 2</label>
              <input
                type="text"
                value={params.bankLabel2 ?? ''}
                onChange={(e) => setManualFields({ bankLabel2: e.target.value || undefined })}
                placeholder="Saldo Santander"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Saldo banco 2</label>
              <input
                type="number"
                value={params.bankBalance2 ?? ''}
                onChange={(e) =>
                  setManualFields({
                    bankBalance2: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                placeholder="0.00"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <Loading />
      ) : !report ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
          No hay datos para este periodo.
        </div>
      ) : (
        <div className="space-y-6">
          <p className="text-sm text-slate-500 capitalize">{report.monthLabel}</p>

          {/* Health cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium uppercase tracking-wide text-[#639922]">
                  Total Ingresos
                </span>
                <ArrowUpRight size={16} className="text-[#639922]" />
              </div>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(report.totalIncome)}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium uppercase tracking-wide text-[#E24B4A]">
                  Total Egresos
                </span>
                <ArrowDownRight size={16} className="text-[#E24B4A]" />
              </div>
              <p className="text-2xl font-bold text-slate-800">
                {formatCurrency(report.totalExpenses)}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <span className="text-xs font-medium uppercase tracking-wide text-[#7F77DD]">
                Flujo del mes
              </span>
              <p
                className={`text-2xl font-bold ${
                  report.summary.cashFlow >= 0 ? 'text-slate-800' : 'text-[#E24B4A]'
                }`}
              >
                {formatCurrency(report.summary.cashFlow)}
              </p>
            </div>
          </div>

          <StatementSection
            title="Ingresos"
            groups={report.income}
            total={report.totalIncome}
            totalLabel="Total Ingresos"
            accent="green"
          />

          <StatementSection
            title="Egresos"
            groups={report.expenses}
            total={report.totalExpenses}
            totalLabel="Total Egresos"
            accent="red"
          />

          {/* Summary */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100">
              <span className="font-semibold text-slate-800">Resumen</span>
            </div>
            <div className="divide-y divide-slate-100">
              {[
                { label: 'Saldo inicial', value: report.summary.openingBalance, strong: false },
                { label: 'Total ingresos', value: report.summary.totalIncome, strong: false },
                { label: 'Total egresos', value: report.summary.totalExpenses, strong: false },
                { label: 'Flujo del mes', value: report.summary.cashFlow, strong: false },
                { label: 'Saldo final', value: report.summary.closingBalance, strong: true },
                ...report.summary.bankBalances.map((b) => ({
                  label: b.label,
                  value: b.amount,
                  strong: false,
                })),
              ].map((row, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-2.5">
                  <span className={`text-sm ${row.strong ? 'font-bold text-slate-800' : 'text-slate-600'}`}>
                    {row.label}
                  </span>
                  <span
                    className={`text-sm tabular-nums ${row.strong ? 'font-bold text-slate-800 text-base' : 'text-slate-700'}`}
                  >
                    {formatCurrency(row.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
